import type { TMessage } from '#app/domain/chat/types';

export const setMessage = (messagesList: TMessage[], newItem: TMessage) => {
    if (!newItem || !newItem.id) return;

    const replaceMessageInList = (index: number) => ({ ...messagesList[index], ...newItem });

    const getElementIndex = (
        property: string,
        externalItem: TMessage = newItem,
        list: TMessage[] = messagesList
    ): number => {
        if (Object.prototype.hasOwnProperty.call(externalItem, property)) {
            return list.findIndex((internalItem: TMessage): boolean =>
                (internalItem as any)[property]
                    ? (internalItem as any)[property] == (externalItem as any)[property]
                    : false
            );
        }
        return -1;
    };

    const updateIfExists = (key: string) => {
        const index = getElementIndex(key, newItem);
        if (index === -1) return false;
        messagesList[index] = replaceMessageInList(index);
        return true;
    };

    const updateByServerMessageId = () => {
        const externalServerId = String((newItem as any)?.message_id ?? (newItem as any)?.id ?? "");
        if (!externalServerId) return false;
        const index = messagesList.findIndex(
            (msg) => String((msg as any)?.server_message_id ?? "") === externalServerId
        );
        if (index === -1) return false;
        messagesList[index] = replaceMessageInList(index);
        return true;
    };

    const reconcileSentWithPending = () => {
        if (newItem.viewType !== "message") return false;
        const status = String(newItem.status ?? "");
        if (["wait", "loading", "pending", "uploading"].includes(status)) return false;
        if (String(newItem.chat_id ?? "") === "") return false;

        const nowTs = Number(newItem.timestamp ?? (newItem.date_time ? Date.parse(newItem.date_time) : Date.now()));
        const pendingIndexes: number[] = [];
        for (let i = 0; i < messagesList.length; i++) {
            const msg = messagesList[i];
            if (msg.viewType !== "message") continue;
            if (String(msg.chat_id ?? "") !== String(newItem.chat_id ?? "")) continue;
            if (msg.is_echo !== 1) continue;

            const msgStatus = String(msg.status ?? "");
            if (!["wait", "loading", "pending", "uploading"].includes(msgStatus)) continue;

            const msgTs = Number(msg.timestamp ?? (msg.date_time ? Date.parse(msg.date_time) : 0));
            if (Number.isNaN(msgTs)) continue;
            const delta = Math.abs(nowTs - msgTs);
            if (delta > 180000) continue;

            pendingIndexes.push(i);
        }

        if (!pendingIndexes.length) return false;

        // Берём самый близкий по времени pending в текущем чате.
        let replaceIndex = pendingIndexes[0];
        let bestDelta = Number.POSITIVE_INFINITY;
        for (const idx of pendingIndexes) {
            const msg = messagesList[idx];
            const msgTs = Number(msg.timestamp ?? (msg.date_time ? Date.parse(msg.date_time) : 0));
            const delta = Math.abs(nowTs - msgTs);
            if (delta < bestDelta) {
                bestDelta = delta;
                replaceIndex = idx;
            }
        }
        messagesList[replaceIndex] = replaceMessageInList(replaceIndex);

        // Удаляем остальные pending-дубли (если были), кроме заменённого.
        for (let i = pendingIndexes.length - 1; i >= 0; i--) {
            const idx = pendingIndexes[i];
            if (idx === replaceIndex) continue;
            messagesList.splice(idx, 1);
        }

        return true;
    };

    if (
        updateIfExists("local_message_id") ||
        updateIfExists("message_id") ||
        updateIfExists("id") ||
        updateByServerMessageId() ||
        reconcileSentWithPending()
    ) return;

    const updatePendingOutgoing = () => {
        if (newItem.viewType !== "message") return false;
        const nowTs = Number(newItem.timestamp ?? (newItem.date_time ? Date.parse(newItem.date_time) : Date.now()));
        const index = messagesList.findIndex((msg) => {
            if (msg.viewType !== "message") return false;
            if (msg.is_echo !== 1) return false;
            if (!["wait", "sent", "delivered"].includes(String(msg.status ?? ""))) return false;
            if (String(msg.chat_id ?? "") !== String(newItem.chat_id ?? "")) return false;

            const normalizeText = (value: any) => String(value ?? "").trim();
            const sameText = normalizeText(msg.text) === normalizeText(newItem.text);
            const sameFileName = String((msg.file as any)?.name ?? "") === String((newItem.file as any)?.name ?? "");
            if (!sameText && !sameFileName) return false;

            const msgTs = Number(msg.timestamp ?? (msg.date_time ? Date.parse(msg.date_time) : 0));
            return !Number.isNaN(msgTs) && Math.abs(nowTs - msgTs) < 120000;
        });

        if (index === -1) return false;
        messagesList[index] = replaceMessageInList(index);
        return true;
    };

    if (updatePendingOutgoing()) return;

    if (newItem.viewType === "message") {
        const setIdToNumber = (id: string | null | undefined): number => Number(id ?? null);
        const messageItems: TMessage[] = messagesList
            .filter((item: TMessage): boolean => item.viewType === "message")
            .sort((firstMessage: TMessage, secondMessage: TMessage) => setIdToNumber(firstMessage.id) - setIdToNumber(secondMessage.id));

        const lastItemId = setIdToNumber(messageItems[messageItems.length - 1]?.id);
        const firstItemId = setIdToNumber(messageItems[0]?.id);
        const newItemId = setIdToNumber(newItem.id);
        const canOrderByNumericId =
            !Number.isNaN(newItemId) &&
            (messageItems.length === 0 ||
                (!Number.isNaN(lastItemId) && !Number.isNaN(firstItemId)));

        if (!messageItems.length || !canOrderByNumericId || newItemId > lastItemId) {
            messagesList.push(newItem);
        } else if (newItemId < firstItemId) {
            messagesList.unshift(newItem);
        } else {
            let inserted = false;
            for (let i = 0; i < messageItems.length; i++) {
                const itemId = setIdToNumber(messageItems[i].id);
                if (!Number.isNaN(itemId) && newItemId < itemId) {
                    messagesList.splice(messagesList.indexOf(messageItems[i]), 0, newItem);
                    inserted = true;
                    break;
                }
            }
            if (!inserted) {
                messagesList.push(newItem);
            }
        }
    } else {
        messagesList.push(newItem);
    }
};

export const setMessages = (state: TMessage[], messages: TMessage[], chatId: string): TMessage[] => {
    const newState: TMessage[] = [...state];
    let lastDate: string | null = null;

    messages.forEach((message) => {
        if (message.date_time) {
            const messageDate = message.date_time.split(" ")[0];
            message.viewType = "message";

            if (messageDate !== lastDate) {
                setMessage(newState, {
                    id: messageDate,
                    chat_id: chatId,
                    message_id: messageDate,
                    date_time: messageDate,
                    viewType: "separator",
                });
                lastDate = messageDate ?? null;
            }
        }

        setMessage(newState, message);
    });

    return newState;
};

const resolveMessageIdentity = (
    raw: any,
    chatId: string
): { id?: string; messageId?: string } => {
    const normalizedChatId = String(raw?.chat_id ?? chatId);
    const chatIdDigits = normalizedChatId.replace(/\D/g, "");
    const rawMessageId = raw?.message_id ?? raw?.id;
    const rawIdStr = rawMessageId != null ? String(rawMessageId) : "";
    const isChatIdCollision =
        rawIdStr !== "" &&
        (rawIdStr === normalizedChatId || (chatIdDigits !== "" && rawIdStr === chatIdDigits));

    let messageId: string | undefined;
    if (!isChatIdCollision && rawIdStr !== "") {
        messageId = rawIdStr;
    } else if (raw?.local_message_id != null) {
        messageId = String(raw.local_message_id);
    }

    // id — для сортировки/вставки в список; предпочитаем numeric timestamp/id
    let id: string | undefined;
    if (raw?.timestamp != null && !Number.isNaN(Number(raw.timestamp))) {
        id = String(raw.timestamp);
    } else if (raw?.id != null && !Number.isNaN(Number(raw.id))) {
        id = String(raw.id);
    } else if (messageId) {
        id = messageId;
    } else {
        const dateTime = raw?.date_time ?? raw?.created_at ?? raw?.datetime;
        if (dateTime) {
            const parsed = Date.parse(String(dateTime));
            if (!Number.isNaN(parsed)) {
                id = String(parsed);
            }
        }
    }

    if (!messageId) {
        messageId = id;
    }

    return { id, messageId };
};

export const normalizeMessage = (raw: any, currentUserId: number, chatId: string): TMessage => {
    const { id, messageId } = resolveMessageIdentity(raw, chatId);
    const senderId = raw?.sender_id ?? raw?.senderId ?? raw?.sender;
    const authorIdRaw = raw?.author_id ?? raw?.authorId;
    const authorIdStr = authorIdRaw != null ? String(authorIdRaw) : "";
    const isOwnByAuthorId = authorIdStr.endsWith(`-${currentUserId}`) || authorIdStr === String(currentUserId);

    const isEchoFromRaw = raw?.is_echo;
    const computedIsEcho =
        (senderId != null && Number(senderId) === Number(currentUserId)) || isOwnByAuthorId ? 1 : 0;

    return {
        id: id != null ? String(id) : undefined,
        local_message_id: raw?.local_message_id != null ? String(raw.local_message_id) : undefined,
        message_id: messageId != null ? String(messageId) : undefined,
        chat_id: raw?.chat_id != null ? String(raw.chat_id) : String(chatId),
        type: raw?.type ?? "text",
        text: raw?.text ?? "",
        status: raw?.status,
        date_time: raw?.date_time ?? raw?.created_at ?? raw?.datetime,
        timestamp:
            raw?.timestamp != null && !Number.isNaN(Number(raw.timestamp))
                ? Number(raw.timestamp)
                : undefined,
        content_uri: raw?.content_uri ?? raw?.file_path ?? raw?.file_url ?? raw?.file_path_url ?? "",
        file: raw?.file ?? (raw?.file_name ? { name: raw.file_name } : undefined),
        is_echo: isEchoFromRaw === 0 || isEchoFromRaw === 1 ? Number(isEchoFromRaw) : computedIsEcho,
        quoted_message: raw?.quoted_message ?? null,
        viewType: "message",
    };
};

export const sortMessagesByDate = (messages: TMessage[]) => {
    const statusRank = (status: string | undefined | null): number => {
        const normalized = String(status ?? "");
        if (normalized === "read") return 5;
        if (normalized === "delivered") return 4;
        if (normalized === "sent") return 3;
        if (normalized === "wait") return 2;
        if (normalized === "loading" || normalized === "pending" || normalized === "uploading") return 1;
        return 0;
    };

    const dedupe = (list: TMessage[]): TMessage[] => {
        const byStableKey = new Map<string, TMessage>();

        for (const message of list) {
            if (message.viewType !== "message") {
                byStableKey.set(`separator-${String(message.id ?? Math.random())}`, message);
                continue;
            }

            const timePart = String(
                message.timestamp ??
                    (message.date_time ? Date.parse(message.date_time) : "") ??
                    ""
            );
            const textPart = String(message.text ?? "").trim().slice(0, 24);
            const stableKey =
                message.local_message_id
                    ? `local-${message.local_message_id}`
                    : (message as any).server_message_id
                      ? `server-${String((message as any).server_message_id)}`
                      : message.message_id
                        ? `message-${String(message.message_id)}-${String(message.chat_id ?? "")}-${timePart}-${textPart}`
                        : `id-${String(message.id ?? "")}-${String(message.chat_id ?? "")}-${timePart}-${textPart}`;

            const existing = byStableKey.get(stableKey);
            if (!existing) {
                byStableKey.set(stableKey, message);
                continue;
            }

            const existingRank = statusRank(existing.status);
            const nextRank = statusRank(message.status);
            byStableKey.set(stableKey, nextRank >= existingRank ? message : existing);
        }

        const collapsed = Array.from(byStableKey.values());

        // Дополнительное схлопывание "wait + sent" в одном чате по похожей полезной нагрузке.
        const result: TMessage[] = [];
        const isPendingStatus = (status: string | undefined | null) =>
            ["wait", "loading", "pending", "uploading"].includes(String(status ?? ""));
        const isFinalStatus = (status: string | undefined | null) =>
            ["sent", "delivered", "read", "error", "inbound"].includes(String(status ?? ""));

        for (const message of collapsed) {
            if (message.viewType !== "message") {
                result.push(message);
                continue;
            }

            const idx = result.findIndex((existing) => {
                if (existing.viewType !== "message") return false;
                if (String(existing.chat_id ?? "") !== String(message.chat_id ?? "")) return false;

                const firstTs = Number(existing.timestamp ?? (existing.date_time ? Date.parse(existing.date_time) : 0));
                const secondTs = Number(message.timestamp ?? (message.date_time ? Date.parse(message.date_time) : 0));
                if (Number.isNaN(firstTs) || Number.isNaN(secondTs)) return false;
                const closeInTime = Math.abs(firstTs - secondTs) < 180000;
                if (!closeInTime) return false;

                // Жёсткое правило: в рамках одного чата и окна времени для исходящих
                // оставляем только финальный статус, pending скрываем.
                const bothOwn = Number(existing.is_echo ?? 0) === 1 && Number(message.is_echo ?? 0) === 1;
                if (bothOwn) {
                    const pendingVsFinal =
                        (isPendingStatus(existing.status) && isFinalStatus(message.status)) ||
                        (isFinalStatus(existing.status) && isPendingStatus(message.status));
                    if (pendingVsFinal) return true;
                }

                const sameText = String(existing.text ?? "").trim() === String(message.text ?? "").trim();
                const sameFileName = String((existing.file as any)?.name ?? "") === String((message.file as any)?.name ?? "");
                return sameText || sameFileName;
            });

            if (idx === -1) {
                result.push(message);
                continue;
            }

            const existing = result[idx];
            result[idx] = statusRank(message.status) >= statusRank(existing.status) ? message : existing;
        }

        return result;
    };

    const resolveSortTime = (message: TMessage): number => {
        if (message.date_time) {
            const parsed = new Date(message.date_time).getTime();
            if (!Number.isNaN(parsed)) return parsed;
        }
        if (message.timestamp != null && !Number.isNaN(Number(message.timestamp))) {
            return Number(message.timestamp);
        }
        const numericId = Number(message.id ?? NaN);
        return Number.isNaN(numericId) ? 0 : numericId;
    };

    return dedupe(messages).sort(
        (firstMessage: TMessage, lastMessage: TMessage) =>
            resolveSortTime(firstMessage) - resolveSortTime(lastMessage)
    );
};
